import type { ExcelDownloadClient } from "app/components/React/ExcelButton/ExcelClientDownload";
import type {
    AppsExcelDownloadRequestDto,
    AppsSearchRequestDto,
    AppsSearchResponseDto,
} from "app/pages/sales-intelligence/modules/apps-search/types/dto";
import type {
    LeadsExcelDownloadRequestDto,
    LeadsSearchRequestDto,
    LeadsSearchResponseDto,
} from "app/pages/sales-intelligence/modules/leads-search";
import type {
    ContactsResponseDto,
    ContactsSearchResultsRequestDto,
} from "app/pages/sales-intelligence/sub-modules/contacts-search/types/dto";
import type { CrmListObjectType, Integration } from "app/pages/sales-intelligence/sub-modules/management/types";
import type { IFetchService, PostConfigObject } from "app/services/fetchService";

function createCrmListWebsitesApiService(fetchService: IFetchService, excelDownloadClient: ExcelDownloadClient) {
    const createCrmListUrl = (crm: Integration, objectType: CrmListObjectType): string =>
        `/sales-api/crm-list/${crm}/websites/${objectType}`;

    return {
        fetchSearchResults(
            crm: Integration,
            objectType: CrmListObjectType,
            dto: LeadsSearchRequestDto,
            config?: PostConfigObject,
        ): Promise<LeadsSearchResponseDto> {
            return fetchService.post(createCrmListUrl(crm, objectType), dto, config);
        },

        exportToExcel(crm: Integration, objectType: CrmListObjectType, dto: LeadsExcelDownloadRequestDto) {
            return excelDownloadClient(`${createCrmListUrl(crm, objectType)}/excel`, dto);
        },

        fetchContacts(
            crm: Integration,
            objectType: CrmListObjectType,
            dto: ContactsSearchResultsRequestDto,
            config?: PostConfigObject,
        ): Promise<ContactsResponseDto> {
            return fetchService.post(`${createCrmListUrl(crm, objectType)}/contacts`, dto, config);
        },
    };
}

function createCrmListCompaniesApiService(fetchService: IFetchService, excelDownloadClient: ExcelDownloadClient) {
    const createCrmListUrl = (crm: Integration, objectType: CrmListObjectType): string =>
        `/sales-api/crm-list/${crm}/companies/${objectType}`;

    return {
        fetchSearchResults(
            crm: Integration,
            objectType: CrmListObjectType,
            dto: LeadsSearchRequestDto,
            config?: PostConfigObject,
        ): Promise<LeadsSearchResponseDto> {
            return fetchService.post(createCrmListUrl(crm, objectType), dto, config);
        },

        exportToExcel(crm: Integration, objectType: CrmListObjectType, dto: LeadsExcelDownloadRequestDto) {
            return excelDownloadClient(`${createCrmListUrl(crm, objectType)}/excel`, dto);
        },

        fetchContacts(
            crm: Integration,
            objectType: CrmListObjectType,
            dto: ContactsSearchResultsRequestDto,
            config?: PostConfigObject,
        ): Promise<ContactsResponseDto> {
            return fetchService.post(`${createCrmListUrl(crm, objectType)}/contacts`, dto, config);
        },
    };
}

function createCrmListAppsApiService(fetchService: IFetchService, excelDownloadClient: ExcelDownloadClient) {
    const createCrmListUrl = (crm: Integration, objectType: CrmListObjectType): string =>
        `/sales-api/crm-list/${crm}/apps/${objectType}`;

    return {
        fetchSearchResults(
            crm: Integration,
            objectType: CrmListObjectType,
            dto: AppsSearchRequestDto,
            config?: PostConfigObject,
        ): Promise<AppsSearchResponseDto> {
            return fetchService.post(createCrmListUrl(crm, objectType), dto, config);
        },

        exportToExcel(crm: Integration, objectType: CrmListObjectType, dto: AppsExcelDownloadRequestDto) {
            return excelDownloadClient(`${createCrmListUrl(crm, objectType)}/excel`, dto);
        },
    };
}

export function createCrmListApiService(fetchService: IFetchService, excelDownloadClient: ExcelDownloadClient) {
    return {
        apps: createCrmListAppsApiService(fetchService, excelDownloadClient),
        companies: createCrmListCompaniesApiService(fetchService, excelDownloadClient),
        websites: createCrmListWebsitesApiService(fetchService, excelDownloadClient),
    };
}
